// Water on the screen when the profile ship sinks: drops appear all over the
// view as if a wave splashed the glass, slide down in fits and starts leaving
// a faint wet streak, and dry up. Each drop is a small element that refracts
// the page behind it (backdrop blur and brightening, shaded inside its edge
// like a lens), so it looks like water rather than a painted bubble. A fixed
// layer over the page that removes itself when the last drop has dried; it
// never catches a click.

interface Drop {
  el: HTMLDivElement;
  trail: HTMLDivElement | null;
  x: number;
  y: number;
  r: number;
  /** When it lands, seconds after the splash. */
  at: number;
  speed: number;
  running: boolean;
  nextChange: number;
  /** Where its streak starts (the top of where it ran). */
  trailTop: number;
  dryAt: number;
  dryFor: number;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

function makeDrop(r: number): HTMLDivElement {
  const el = document.createElement('div');
  const d = r * 2;
  const lens = 'blur(1.2px) brightness(1.07) saturate(1.25)';
  Object.assign(el.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    width: `${d}px`,
    height: `${d}px`,
    marginLeft: `${-r}px`,
    marginTop: `${-r}px`,
    borderRadius: '50%',
    opacity: '0',
    willChange: 'transform, opacity',
    // the lens: what is behind looks a touch softer, brighter and richer
    backdropFilter: lens,
    // light focused low inside the drop
    background: 'radial-gradient(ellipse at 50% 72%, rgba(255,255,255,0.28), rgba(255,255,255,0) 58%)',
    // shadowed along the top inside edge, bright along the bottom, a soft contact shadow under it
    boxShadow: [
      `inset 0 ${r * 0.28}px ${r * 0.32}px rgba(20,22,24,0.28)`,
      `inset 0 ${-r * 0.22}px ${r * 0.3}px rgba(255,255,255,0.55)`,
      `0 ${Math.max(1, r * 0.08)}px ${Math.max(1.5, r * 0.18)}px rgba(20,22,24,0.18)`,
    ].join(', '),
  });
  el.style.setProperty('-webkit-backdrop-filter', lens);
  // the small, sharp reflection of a light source
  const glint = document.createElement('span');
  Object.assign(glint.style, {
    position: 'absolute',
    left: '24%',
    top: '18%',
    width: `${Math.max(1.5, r * 0.32)}px`,
    height: `${Math.max(1, r * 0.2)}px`,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.85)',
    transform: 'rotate(-25deg)',
    filter: `blur(${Math.max(0.2, r * 0.03)}px)`,
  });
  el.appendChild(glint);
  return el;
}

function makeTrail(width: number): HTMLDivElement {
  const el = document.createElement('div');
  const wet = 'blur(0.6px) brightness(1.03)';
  Object.assign(el.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    width: `${width}px`,
    marginLeft: `${-width / 2}px`,
    height: '0',
    borderRadius: `${width}px`,
    // a wet streak: only the faintest change in the page under it
    backdropFilter: wet,
    background: 'linear-gradient(to bottom, rgba(20,22,24,0), rgba(20,22,24,0.06))',
    boxShadow: 'inset 0 0 1px rgba(255,255,255,0.35)',
  });
  el.style.setProperty('-webkit-backdrop-filter', wet);
  return el;
}

/** Splashes the screen; resolves when every drop has dried and the layer is gone. */
export function splashScreen(seconds = 10): Promise<void> {
  return new Promise((resolve) => {
    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.className = 'pf-splash';
    Object.assign(layer.style, { position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '1000', overflow: 'hidden' });
    document.body.appendChild(layer);

    const w = window.innerWidth;
    const h = window.innerHeight;
    const area = (w * h) / (1280 * 800);
    const count = Math.round(180 * Math.min(1.6, Math.max(0.45, area)));
    const drops: Drop[] = [];
    for (let i = 0; i < count; i++) {
      // most land in the first moment, a few stragglers after; mostly small, some big
      const at = Math.random() < 0.75 ? rand(0, 0.7) : rand(0.7, 2.2);
      const big = Math.random() < 0.3;
      const r = big ? rand(9, 17) : rand(2.5, 8);
      const el = makeDrop(r);
      layer.appendChild(el);
      const y = rand(-h * 0.03, h);
      drops.push({
        el,
        trail: null,
        x: rand(0, w),
        y,
        r,
        at,
        // the heavier the drop, the faster it runs
        speed: rand(45, 95) * (r / 10),
        running: false,
        nextChange: at + rand(0.05, 0.6),
        trailTop: y,
        dryAt: rand(seconds * 0.45, seconds * 0.8),
        dryFor: rand(1.4, 2.8),
      });
    }

    let t = 0;
    let last = 0;
    const step = (now: number) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
      last = now;
      t += dt;
      let alive = 0;
      for (const d of drops) {
        if (t < d.at) {
          alive++;
          continue;
        }
        const dry = Math.max(0, Math.min(1, (t - d.dryAt) / d.dryFor));
        if (dry >= 1) {
          d.el.remove();
          d.trail?.remove();
          continue;
        }
        alive++;
        // all but the smallest drops run down, in long slides with short pauses
        if (d.r > 4.5 && t > d.nextChange) {
          d.running = !d.running;
          d.nextChange = t + (d.running ? rand(0.8, 2.4) : rand(0.15, 0.6));
          if (d.running && !d.trail) {
            d.trail = makeTrail(d.r * 0.7);
            layer.insertBefore(d.trail, layer.firstChild);
            d.trailTop = d.y;
          }
        }
        if (d.running) {
          d.y += d.speed * dt;
          d.x += Math.sin(t * 2.3 + d.r) * 5 * dt;
        }
        // lands with a tiny spread, then holds; a little longer than round while it runs
        const land = Math.min(1, (t - d.at) / 0.1);
        const scale = (0.7 + 0.3 * land) * (1 - dry * 0.35);
        const stretch = d.running ? 1.18 : 1;
        d.el.style.opacity = String(land * (1 - dry));
        d.el.style.transform = `translate3d(${d.x.toFixed(1)}px, ${d.y.toFixed(1)}px, 0) scale(${scale.toFixed(3)}, ${(scale * stretch).toFixed(3)})`;
        if (d.trail) {
          const height = Math.max(0, d.y - d.r * 0.6 - d.trailTop);
          d.trail.style.height = `${height.toFixed(1)}px`;
          d.trail.style.transform = `translate3d(${d.x.toFixed(1)}px, ${d.trailTop.toFixed(1)}px, 0)`;
          d.trail.style.opacity = String(1 - dry);
        }
      }
      if (alive > 0 && t < seconds + 4) {
        requestAnimationFrame(step);
      } else {
        layer.remove();
        resolve();
      }
    };
    requestAnimationFrame(step);
  });
}
